// ─────────────────────────────────────────────────────────────────────
// ProtectedRoute.jsx
// Wrap any route that requires authentication or a specific tier.
//
// Usage in App.js:
//   <Route path="/dashboard" element={
//     <ProtectedRoute><DashboardPage/></ProtectedRoute>
//   }/>
//
//   <Route path="/tools/ai" element={
//     <ProtectedRoute requireTier="ai_tools"><AITools/></ProtectedRoute>
//   }/>
// ─────────────────────────────────────────────────────────────────────
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import AdUnit from '../components/AdUnit';
const TIER_ORDER = { free:0, ai_tools:1, ai_tools_plus:2, ai_premium:3 };

export function ProtectedRoute({ children, requireTier = null,requireVerified = true }) {
  const { isAuthenticated, tier,user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div style={{ minHeight:'100vh', background:'#07080c', display:'flex', alignItems:'center', justifyContent:'center' }}>
        <div style={{ width:20, height:20, borderRadius:'50%', border:'2px solid rgba(255,255,255,.1)', borderTopColor:'#00c8f0', animation:'spin .65s linear infinite' }}/>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace/>;
  }
    if (requireVerified && user && user.email_verified === false) {
    return <Navigate to="/verify-required" replace/>;
  }

  if (requireTier && TIER_ORDER[tier] < TIER_ORDER[requireTier]) {
    return <Navigate to="/pricing" state={{ requiredTier: requireTier }} replace/>;
  }

  return children;
}


// ─────────────────────────────────────────────────────────────────────
// AdBanner.jsx
// Show ads to free users, nothing to paid users.
//
// Usage:
//   <AdBanner slot="tools-header"/>
//   <AdBanner slot="sidebar" style={{ marginTop:20 }}/>
// ─────────────────────────────────────────────────────────────────────
export function AdBanner({ slot, style = {} }) {
  const { tier } = useAuth();

  // Paid users never see ads
  if (tier !== 'free') return null;

  return (
    <div style={{
      padding:'10px 14px',
      background:'rgba(255,255,255,.02)',
      border:'1px solid rgba(255,255,255,.06)',
      borderRadius:8, textAlign:'center',
      fontSize:11.5, color:'rgba(220,230,250,.25)',
      fontFamily:'"DM Sans",system-ui,sans-serif',
      ...style,
    }}>
      {/* Replace with your real ad unit e.g. Google AdSense */}
      {/* <ins className="adsbygoogle" data-ad-slot={slot}/> */}
      {/* <div style={{ minHeight:60, display:'flex', alignItems:'center', justifyContent:'center' }}> */}
        <AdUnit/>

        {/* Advertisement · <a href="/pricing" style={{ color:'#00c8f0', marginLeft:6, textDecoration:'none' }}>Remove ads →</a> */}
      {/* </div> */}

    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// UpgradePrompt.jsx
// Show when a user tries to use a locked feature.
//
// Usage:
//   <UpgradePrompt
//     feature="AI Tools"
//     requiredTier="ai_tools"
//     description="Generate emails, SQL, LinkedIn posts and more with AI"
//   />
// ─────────────────────────────────────────────────────────────────────
export function UpgradePrompt({ feature, requiredTier, description }) {
  const T = {
    bg2:'#0c0f18', border:'rgba(255,255,255,.07)',
    white:'#dce6fa', muted:'rgba(220,230,250,.45)',
    cyan:'#00c8f0', yellow:'#f1c40f',
    sans:'"Syne","DM Sans",system-ui,sans-serif',
  };
  const color = requiredTier === 'ai_premium' ? T.yellow : T.cyan;

  return (
    <div style={{
      padding:'32px 24px', textAlign:'center',
      background:T.bg2, border:`1px solid ${color}25`,
      borderRadius:12,
      fontFamily:T.sans,
    }}>
      <div style={{ fontSize:32, marginBottom:10 }}>🔒</div>
      <div style={{ fontSize:16, fontWeight:700, color:T.white, marginBottom:6 }}>
        {feature} is a paid feature
      </div>
      {description && (
        <div style={{ fontSize:13, color:T.muted, marginBottom:20, maxWidth:360, margin:'0 auto 20px' }}>
          {description}
        </div>
      )}
      <a href="/pricing" style={{
        display:'inline-block', padding:'10px 24px', borderRadius:8,
        border:`1px solid ${color}50`, background:`${color}15`,
        color, fontSize:13, fontWeight:700, textDecoration:'none',
      }}>
        Upgrade to unlock →
      </a>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// TierGate.jsx
// Conditionally render children based on subscription tier.
//
// Usage:
//   <TierGate requires="ai_tools" fallback={<UpgradePrompt feature="AI Tools" requiredTier="ai_tools"/>}>
//     <AITool/>
//   </TierGate>
// ─────────────────────────────────────────────────────────────────────
export function TierGate({ requires, children, fallback = null }) {
  const { tier } = useAuth();
  const TIER_ORDER = { free:0, ai_tools:1, ai_tools_plus:2, ai_premium:3 };
  if (TIER_ORDER[tier] >= TIER_ORDER[requires]) return children;
  return fallback;
}


// ─────────────────────────────────────────────────────────────────────
// NavUserMenu.jsx
// Drop-in user menu for your existing navbar.
//
// Usage in your Navbar component:
//   import { NavUserMenu } from './ProtectedRoute';
//   <NavUserMenu/>
// ─────────────────────────────────────────────────────────────────────
export function NavUserMenu() {
  const { user, tier, logout,subscription, isAuthenticated } = useAuth();
  console.log(user)
  const [open, setOpen] = React.useState(false);
  const TIER_LABELS = {
  free:          'Free',
  ai_tools:      'AI Tool',
  ai_tools_plus: 'AI + Chrome Ext',
  ai_premium:    'AI Premium',
};
const isTrial = subscription?.status === 'trialing';
const displayLabel = isTrial ? '🎁 Trial' : (TIER_LABELS[tier] || 'Free');
  const T = {
    bg2:'#0c0f18', surf:'#161c2a',
    border:'rgba(255,255,255,.07)', borderH:'rgba(255,255,255,.13)',
    white:'#dce6fa', muted:'rgba(220,230,250,.45)',
    cyan:'#00c8f0', purple:'#b78cff',
    sans:'"DM Sans",system-ui,sans-serif',
  };
  const TIER_COLORS = { free:T.muted, ai_tools:T.cyan, ai_tools_plus:'#f1c40f', ai_premium:T.purple };


const color = TIER_COLORS[tier] || T.muted;

  if (!isAuthenticated) {
    return (
      <a href="/login" style={{
        padding:'6px 14px', borderRadius:7,
        border:`1px solid rgba(0,200,240,.4)`,
        background:'rgba(0,200,240,.1)', color:T.cyan,
        fontSize:13, fontWeight:600, fontFamily:T.sans, textDecoration:'none',
      }}>Sign In</a>
    );
  }

  return (
    <div style={{ position:'relative' }}>
      <button onClick={() => setOpen(p => !p)} style={{
        display:'flex', alignItems:'center', gap:8,
        padding:'5px 10px', borderRadius:8,
        border:`1px solid ${open ? T.borderH : T.border}`,
        background: open ? T.surf : 'transparent',
        cursor:'pointer', fontFamily:T.sans,
      }}>
        <div style={{
          width:28, height:28, borderRadius:'50%',
          background:`${color}20`, border:`1px solid ${color}40`,
          display:'flex', alignItems:'center', justifyContent:'center',
          fontSize:12, fontWeight:700, color,
        }}>
          {user?.name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || '?'}
        </div>
        <span style={{ fontSize:12, color:T.muted, maxWidth:120, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
          {user?.name || user?.email}
        </span>
        <span style={{ fontSize:10, color:T.muted }}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div style={{
          position:'absolute', top:'calc(100% + 8px)', right:0, minWidth:180, zIndex:50,
          background:T.bg2, border:`1px solid ${T.border}`, borderRadius:10,
          padding:'6px', boxShadow:'0 8px 30px rgba(0,0,0,.4)',
        }}>
          <div style={{ padding:'8px 10px', borderBottom:`1px solid ${T.border}`, marginBottom:4 }}>
            <div style={{ fontSize:12, color:T.muted, marginBottom:2 }}>{user?.email}</div>
            <span style={{
              padding:'1px 8px', borderRadius:20, fontSize:10, fontWeight:700,
              background:`${color}18`, border:`1px solid ${color}35`, color,
            }}>{displayLabel}</span>
          </div>
          {[
            { label:'Dashboard',  href:'/dashboard' },
            { label:'Pricing',    href:'/pricing'   },
            { label:'Tools',      href:'/tools'     },
          ].map(item => (
            <a key={item.label} href={item.href} style={{
              display:'block', padding:'8px 10px', borderRadius:7,
              color:T.muted, fontSize:13, textDecoration:'none', fontFamily:T.sans,
            }}
            onMouseEnter={e => e.currentTarget.style.background=T.surf}
            onMouseLeave={e => e.currentTarget.style.background='transparent'}
            onClick={() => setOpen(false)}>
              {item.label}
            </a>
          ))}
          <div style={{ borderTop:`1px solid ${T.border}`, marginTop:4, paddingTop:4 }}>
            <button onClick={() => { logout(); setOpen(false); }} style={{
              display:'block', width:'100%', padding:'8px 10px', borderRadius:7,
              background:'transparent', border:'none', color:T.muted,
              fontSize:13, fontFamily:T.sans, cursor:'pointer', textAlign:'left',
            }}
            onMouseEnter={e => e.currentTarget.style.color='#e74c3c'}
            onMouseLeave={e => e.currentTarget.style.color=T.muted}>
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
